export const DATA_CHARTS_PALETTE = [
  '#D9777A',
  '#6391a7ff',
  '#782022ff',
  '#785a84ff',
  '#FCBA04',
  '#C93C41',
  '#795902ff',
  '#A5E29A',
  '#4cb038ff',
  '#26591cff',
]

export const FALLBACK_COLOR = '#8884d8'

// --- helpers: HEX <-> HSL + hue distance ------------------------------------
const hexToRgb = (hex: string) => {
  let h = hex.trim().replace(/^#/, '')

  // Expand short forms first
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('') // RGB -> RRGGBB
  } else if (h.length === 4) {
    // RGBA -> RRGGBBAA; we'll drop AA below
    h = h
      .split('')
      .map((c) => c + c)
      .join('')
  }

  // If 8 digits (RRGGBBAA), drop alpha
  if (h.length === 8) h = h.slice(0, 6)

  if (h.length !== 6) {
    // fallback to a neutral-ish color instead of throwing
    return { r: 136, g: 132, b: 216 } // matches FALLBACK_COLOR-ish
  }

  // Convert #RRGGBB into red/green/blue numbers (0–255).
  const bigint = parseInt(h, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return { r, g, b }
}

// Return HSL; hue in 0–360, saturation/lightness in 0–100%.
const rgbToHsl = (r: number, g: number, b: number) => {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b)
  let h = 0,
    s = 0,
    l = (max + min) / 2
  const d = max - min
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      default:
        h = (r - g) / d + 4
    }
    h *= 60
  }
  return { h: (h + 360) % 360, s: s * 100, l: l * 100 }
}

// Hue is a circle (0° = 360°). This returns the shortest distance between two hue angles.
const circularHueDistance = (a: number, b: number) => {
  const d = Math.abs(a - b) % 360
  return d > 180 ? 360 - d : d
}

/**
 * Assigns colors to entities from a palette, with fallback to generated colors.
 * @param entities - Array of entities to assign colors to
 * @param idKey - Key to use as the unique identifier for each entity
 * @param nameKey - Key to use for generating colors when palette is exhausted
 * @param palette - Color palette to use (defaults to DATA_CHARTS_PALETTE)
 * @returns Record mapping entity IDs to colors
 */
export const generateStaticColor = <T extends Record<string, any>>(
  entities: T[],
  idKey: keyof T = 'id',
  nameKey: keyof T = 'name',
  palette: string[] = DATA_CHARTS_PALETTE
): Record<string | number, string> => {
  const colorMap: Record<string | number, string> = {}
  const usedColors = new Set<string>()

  // Initialize the result map and a set of colors already used.
  entities.forEach((entity, index) => {
    const id = entity[idKey] as unknown as string | number

    // If we have fewer entities than palette colors, just use the palette
    if (index < palette.length) {
      colorMap[id] = palette[index]
      usedColors.add(palette[index])
    } else {
      // palette exhausted → generate a distinct color
      const entityName = String(entity[nameKey] ?? entity[idKey])
      let attempt = 0
      let c = generateColor(`${entityName}|${id}`, attempt, usedColors, palette)
      // The seed combines name and id so it’s deterministic. If it somehow collides with an existing color, bump attempt and try again
      while (usedColors.has(c) && attempt < 64) {
        attempt++
        c = generateColor(`${entityName}|${id}`, attempt, usedColors, palette)
      }
      colorMap[id] = c
      usedColors.add(c)
    }
  })

  return colorMap
}

/**
 * Simple string hash that returns an unsigned 32-bit int.
 * We need a stable number from a string to seed color choices consistently.
 */
const hashString = (s: string): number => {
  let h = 2166136261 >>> 0
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/**
 * HSL -> HEX
 * Computes RGB from HSL using the standard “triangle wave” and converts each channel to two-digit hex.
 * It’s easy to spread hues evenly (distinct but harmonious), while holding saturation/lightness constant for readability.
 */
const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100
  l /= 100
  const k = (n: number) => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
  const toHex = (x: number) =>
    Math.round(255 * x)
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`
}

// --- DISTINCT color generator (avoids palette + used) -----------------------
/**
 * Generates a distinct color:
 * - Uses golden-angle hue stepping.
 * - Enforces min hue separation from both used colors and the given palette.
 * - Alternates lightness bands for extra separability.
 */
const GOLDEN_ANGLE = 137.50776405003785
const MIN_HUE_DELTA_DEFAULT = 36 // can increase these to even 40–48 if colors still feel too similar
const MIN_HSL_DISTANCE = 22 // composite HSL distance threshold
const SATURATION_BANDS = [70, 58] // alternate saturation as well
const LIGHTNESS_BANDS = [52, 68] // brighter/ darker alternation

/**  A simple formula to measure how far apart two colors feel in HSL space,
 * weighting hue more than saturation/lightness.
 * If the distance is too small, we reject the candidate. */
const hslDistance = (
  h1: number,
  s1: number,
  l1: number,
  h2: number,
  s2: number,
  l2: number
) => {
  // wrap-around hue delta
  const dhRaw = Math.abs(h1 - h2) % 360
  const dh = dhRaw > 180 ? 360 - dhRaw : dhRaw
  const ds = Math.abs(s1 - s2)
  const dl = Math.abs(l1 - l2)
  // weights tuned empirically: hue matters most
  const wH = 0.7,
    wS = 0.5,
    wL = 0.5
  return Math.sqrt((wH * dh) ** 2 + (wS * ds) ** 2 + (wL * dl) ** 2)
}

// Helper to get HSL triple from hex
const hexToHslTriple = (hex: string) => {
  const { r, g, b } = hexToRgb(hex)
  const { h, s, l } = rgbToHsl(r, g, b)
  return [h, s, l] as const
}

/** given a seed and existing colors,
 * find a new color that is far enough from all of them (and from palette colors)
 * by scanning around the hue circle using golden-angle steps. */
const generateDistinctColor = (
  seed: string,
  usedHexColors: Set<string>,
  paletteHex: string[],
  attempt = 0,
  minHueDelta = MIN_HUE_DELTA_DEFAULT
): string => {
  // Precompute “forbidden” HSLs (used + palette)
  const forbiddenHsl: Array<readonly [number, number, number]> = []
  usedHexColors.forEach((hex) => forbiddenHsl.push(hexToHslTriple(hex)))
  for (const hex of paletteHex) forbiddenHsl.push(hexToHslTriple(hex))

  const base = hashString(seed) % 360

  // Keep strict thresholds longer; relax slowly only if needed
  for (let k = 0; k < 720; k++) {
    // relax every 90 tries by 2°, but never below 12°
    const relaxedHue = Math.max(12, minHueDelta - Math.floor(k / 90) * 2)

    const h = (base + k * GOLDEN_ANGLE) % 360
    // alternate both saturation and lightness bands
    const s = SATURATION_BANDS[k % SATURATION_BANDS.length]
    const l = LIGHTNESS_BANDS[(k >> 1) % LIGHTNESS_BANDS.length] // change L half as fast

    // Check against every forbidden color by hue & HSL distance
    let ok = true
    for (let i = 0; i < forbiddenHsl.length; i++) {
      const [fh, fs, fl] = forbiddenHsl[i]
      if (circularHueDistance(h, fh) < relaxedHue) {
        ok = false
        break
      }
      if (hslDistance(h, s, l, fh, fs, fl) < MIN_HSL_DISTANCE) {
        ok = false
        break
      }
    }
    if (!ok) continue

    const hex = hslToHex(h, s, l)
    if (!usedHexColors.has(hex)) return hex
  }

  // Rare fallback
  const h = (base + attempt * GOLDEN_ANGLE) % 360
  return hslToHex(h, SATURATION_BANDS[0], LIGHTNESS_BANDS[0])
}

/** Backward-compatible signature; excludes palette & used colors for clarity. */
const generateColor = (
  seed: string,
  attempt = 0,
  usedHexColors: Set<string> = new Set(),
  paletteHex: string[] = DATA_CHARTS_PALETTE
): string => generateDistinctColor(seed, usedHexColors, paletteHex, attempt)

/**
 * Assigns a unique, stable color to each wheat code:
 * - Uses palette first without duplicates (with hashed starting index).
 * - If palette is exhausted, generates deterministic HSL colors.
 * - Persists mapping in localStorage so colors don’t reshuffle when new codes appear.
 */
export const assignWheatCodeColors = (
  items: { id: number; code: string }[],
  palette: string[] = DATA_CHARTS_PALETTE,
  storageKey = 'wheatCodeColorMap'
): Record<number, string> => {
  const stored: Record<number, string> =
    typeof window !== 'undefined'
      ? JSON.parse(localStorage.getItem(storageKey) || '{}')
      : {}

  const used = new Set<string>(Object.values(stored))
  const map: Record<number, string> = { ...stored }

  // Deterministic order so assignment is repeatable before persistence
  const sorted = [...items].sort((a, b) => a.id - b.id)

  for (const it of sorted) {
    // If it already has a stored color → keep it
    if (map[it.id]) {
      used.add(map[it.id])
      continue
    }

    // Otherwise try to pick from palette starting at a hashed index
    const startIdx = hashString(it.code || String(it.id)) % palette.length
    let chosen: string | undefined
    // Then scan the palette circularly until you find a color not in used
    for (let i = 0; i < palette.length; i++) {
      // Distributes first picks across the palette so the first 10 codes don’t always get the same 10 colors in the same order.
      const candidate = palette[(startIdx + i) % palette.length]
      if (!used.has(candidate)) {
        chosen = candidate
        break
      }
    }

    // Palette full: generate a deterministic extra color
    if (!chosen) {
      let attempt = 0
      let c = generateColor(`${it.code}|${it.id}`, attempt, used, palette)
      while (used.has(c) && attempt < 64) {
        attempt++
        // This yields unlimited, deterministic extra colors and retries on collision
        c = generateColor(`${it.code}|${it.id}`, attempt, used, palette)
      }
      chosen = c
    }

    map[it.id] = chosen!
    used.add(chosen!)
  }
  //   Persist the final map
  if (typeof window !== 'undefined') {
    localStorage.setItem(storageKey, JSON.stringify(map))
  }
  return map
}
